import React from "react";

/** 2 cols mobile → 3 cols tablet/desktop → 6 cols only on very wide screens. */
export const TIMESHEET_KPI_GRID_CLASS =
  "grid grid-cols-2 gap-2 sm:gap-2.5 sm:grid-cols-3 xl:grid-cols-6";

/** Shared shell — fixed slots so skeleton and live cards match across breakpoints. */
export const TIMESHEET_KPI_CARD_SHELL_CLASS =
  "relative flex h-full min-h-[6.25rem] min-w-0 flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white py-2.5 pl-3.5 pr-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:min-h-[5.875rem] sm:py-3 sm:pl-4 sm:pr-3.5";

const Pulse = ({ className = "" }) => (
  <span
    className={`block animate-pulse rounded-md bg-[#E5E7EB] ${className}`}
    aria-hidden
  />
);

const TONE_STYLES = {
  pending: {
    accent: "bg-[#F59E0B]",
    icon: "text-[#F59E0B]",
    value: "text-[#0F172A]",
  },
  approve: {
    accent: "bg-[#0DBBB3]",
    icon: "text-[#0DBBB3]",
    value: "text-[#0F172A]",
  },
  brand: {
    accent: "bg-[#4A6BB6]",
    icon: "text-[#4A6BB6]",
    value: "text-[#0F172A]",
  },
  muted: {
    accent: "bg-[#94A3B8]",
    icon: "text-[#64748B]",
    value: "text-[#0F172A]",
  },
  expired: {
    accent: "bg-[#94A3B8]",
    icon: "text-[#64748B]",
    value: "text-[#0F172A]",
  },
};

const TimesheetKpiCard = ({
  label,
  value,
  unit,
  caption,
  icon: Icon,
  tone = "brand",
  skeleton = false,
  refreshing = false,
  valueTitle,
  captionTitle,
}) => {
  const styles = TONE_STYLES[tone] || TONE_STYLES.brand;
  const hoverClass = skeleton
    ? ""
    : "transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-[#D1D5DB] hover:shadow-[0_4px_12px_rgba(15,23,42,0.06)]";

  const fullValueTitle = valueTitle || (unit ? `${value} ${unit}` : value);
  const fullCaptionTitle = captionTitle || caption;

  return (
    <div
      className={`${TIMESHEET_KPI_CARD_SHELL_CLASS} ${hoverClass} ${
        refreshing ? "opacity-70" : ""
      }`}
      aria-busy={refreshing || skeleton || undefined}
    >
      <span
        className={`absolute bottom-2 left-0 top-2 w-[3px] rounded-r-full ${
          skeleton ? "bg-[#E5E7EB]" : styles.accent
        }`}
        aria-hidden
      />

      <div className="flex h-4 min-h-4 items-start justify-between gap-2">
        {skeleton ? (
          <>
            <Pulse className="h-[11px] w-16" />
            <Pulse className="mt-0.5 h-4 w-4 shrink-0 rounded" />
          </>
        ) : (
          <>
            <p
              className="truncate text-[11px] font-semibold leading-tight text-[#64748B]"
              title={label}
            >
              {label}
            </p>
            {Icon ? (
              <Icon
                className={`mt-0.5 h-4 w-4 shrink-0 ${styles.icon}`}
                aria-hidden
              />
            ) : (
              <span className="h-4 w-4 shrink-0" aria-hidden />
            )}
          </>
        )}
      </div>

      <div className="mt-1 min-h-[2.375rem] min-w-0 sm:min-h-[1.3125rem] sm:flex sm:items-baseline sm:gap-1.5">
        {skeleton ? (
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-1.5">
            <Pulse className="h-5 w-[4.5rem] sm:h-[21px]" />
            <Pulse className="h-[11px] w-14" />
          </div>
        ) : (
          <>
            <span
              className={`block min-w-0 truncate text-[clamp(0.9375rem,3.2vw,1.2rem)] font-bold leading-tight tabular-nums sm:text-[1.15rem] xl:text-[1.2rem] ${styles.value}`}
              title={fullValueTitle}
            >
              {value}
            </span>
            <span
              className="mt-0.5 block shrink-0 truncate text-[10px] font-semibold leading-none text-[#64748B] sm:mt-0 sm:max-w-[40%] sm:text-[11px] xl:max-w-none"
              title={unit || undefined}
            >
              {unit || "\u00A0"}
            </span>
          </>
        )}
      </div>

      <p
        className="mt-1 min-h-[2.125rem] line-clamp-2 text-[10px] font-medium leading-snug text-[#64748B] sm:text-[11px]"
        title={fullCaptionTitle || undefined}
      >
        {skeleton ? (
          <Pulse className="h-[11px] w-full max-w-[9.5rem]" />
        ) : (
          caption || "\u00A0"
        )}
      </p>
    </div>
  );
};

export default TimesheetKpiCard;
